import { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_HEARTBEAT_MS = 4000;
const DEFAULT_STALE_MS = 12000;

const parseLeader = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.tabId || !parsed.ts) return null;
    return parsed;
  } catch {
    return null;
  }
};

export function useTabLeader(scopeKey, options = {}) {
  const heartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS;
  const staleMs = options.staleMs ?? DEFAULT_STALE_MS;
  const tabIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [isLeader, setIsLeader] = useState(false);
  const storageKey = useMemo(() => `pb:leader:${scopeKey || "global"}`, [scopeKey]);

  useEffect(() => {
    if (!scopeKey) {
      setIsLeader(false);
      return;
    }

    const tabId = tabIdRef.current;

    const writeLeader = (now) => {
      const payload = JSON.stringify({ tabId, ts: now });
      try {
        localStorage.setItem(storageKey, payload);
      } catch {
        // localStorage yoksa bu tab leader varsayılır.
      }
    };

    const elect = (forceTakeover = false) => {
      const now = Date.now();
      let current = null;
      try {
        current = parseLeader(localStorage.getItem(storageKey));
      } catch {
        current = null;
      }

      const isStale = !current || now - Number(current.ts || 0) > staleMs;
      const isSelf = current?.tabId === tabId;
      const canTakeover = forceTakeover || isStale || isSelf;

      if (canTakeover) {
        writeLeader(now);
        setIsLeader(true);
        return;
      }
      setIsLeader(false);
    };

    const handleStorage = (event) => {
      if (event.key !== storageKey) return;
      elect(false);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        // Aktif sekme olduğunda hızlı devir için takeover yap.
        elect(true);
      }
    };

    const heartbeat = () => {
      // Görünür sekme liderliği korur/devralır; arkaplan sekme liderliği zorlamaz.
      if (document.visibilityState === "visible") {
        elect(true);
      } else {
        elect(false);
      }
    };

    heartbeat();
    const intervalId = setInterval(heartbeat, heartbeatMs);
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
      try {
        const current = parseLeader(localStorage.getItem(storageKey));
        if (current?.tabId === tabId) {
          localStorage.removeItem(storageKey);
        }
      } catch {
        // noop
      }
      setIsLeader(false);
    };
  }, [scopeKey, storageKey, heartbeatMs, staleMs]);

  return isLeader;
}

export default useTabLeader;
